// =============================================================================
// features/configuracoes/comercial/ContactWindowSection.tsx — Grupo "Fuso e
// janelas" (doc 26 §6.9). Dias + horário + fuso + preview no fuso configurado.
// =============================================================================

import * as React from 'react';

import { Input } from '../../../components/ui/Input';
import { cn } from '../../../lib/cn';
import { FALLBACK_ORG_TIMEZONE } from '../../../lib/orgDateTime';

import { SettingsCard } from './SettingsCard';
import type { DraftErrors, SettingsDraft } from './types';
import { WindowPreview } from './WindowPreview';

const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
  { value: 0, label: 'Dom' },
];

interface ContactWindowSectionProps {
  draft: SettingsDraft;
  errors: DraftErrors;
  isDefault: boolean;
  onChange: (updates: Partial<SettingsDraft>) => void;
  disabled?: boolean;
}

export function ContactWindowSection({
  draft,
  errors,
  isDefault,
  onChange,
  disabled = false,
}: ContactWindowSectionProps): React.JSX.Element {
  const toggleDay = (day: number): void => {
    const days = draft.contactWindow.days.includes(day)
      ? draft.contactWindow.days.filter((d) => d !== day)
      : [...draft.contactWindow.days, day];
    onChange({ contactWindow: { ...draft.contactWindow, days } });
  };

  return (
    <SettingsCard
      title="Fuso e janelas"
      description="Dias e horários em que o time comercial pode contatar leads. Fora da janela, tentativas automáticas não disparam."
      isDefault={isDefault}
    >
      <Input
        id="comercial-timezone"
        label="Fuso horário"
        placeholder={FALLBACK_ORG_TIMEZONE}
        value={draft.timezone}
        disabled={disabled}
        error={errors.timezone}
        hint={errors.timezone ? undefined : `Identificador IANA — ex: ${FALLBACK_ORG_TIMEZONE}`}
        onChange={(e) => onChange({ timezone: e.target.value })}
      />

      <div className="flex flex-col gap-2">
        <span className="font-sans font-medium text-ink" style={{ fontSize: 'var(--text-sm)' }}>
          Dias permitidos
        </span>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Dias da semana permitidos">
          {WEEKDAYS.map((day) => {
            const active = draft.contactWindow.days.includes(day.value);
            return (
              <button
                key={day.value}
                type="button"
                disabled={disabled}
                aria-pressed={active}
                onClick={() => toggleDay(day.value)}
                className={cn(
                  'w-11 h-9 rounded-sm font-sans font-semibold transition-all duration-fast',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  active ? 'text-white' : 'text-ink-3 hover:text-ink',
                )}
                style={{
                  fontSize: 'var(--text-xs)',
                  background: active ? 'var(--brand-azul)' : 'var(--surface-muted)',
                  boxShadow: active ? 'var(--elev-1)' : 'none',
                }}
              >
                {day.label}
              </button>
            );
          })}
        </div>
        {errors.days && (
          <span role="alert" className="text-xs text-danger">
            {errors.days}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          id="comercial-start-time"
          label="Início"
          type="time"
          value={draft.contactWindow.startTime}
          disabled={disabled}
          error={errors.startTime}
          onChange={(e) =>
            onChange({ contactWindow: { ...draft.contactWindow, startTime: e.target.value } })
          }
        />
        <Input
          id="comercial-end-time"
          label="Fim"
          type="time"
          value={draft.contactWindow.endTime}
          disabled={disabled}
          error={errors.endTime}
          onChange={(e) =>
            onChange({ contactWindow: { ...draft.contactWindow, endTime: e.target.value } })
          }
        />
      </div>

      <WindowPreview
        timezone={draft.timezone}
        days={draft.contactWindow.days}
        startTime={draft.contactWindow.startTime}
        endTime={draft.contactWindow.endTime}
      />
    </SettingsCard>
  );
}
